/**
 * StandaloneDehumidifierV1 — Earthy Oasis Light Theme
 *
 * Fullscreen standalone layout for Acuo App.
 * Violet accent (#7c3aed) for dehumidifier identity.
 * category: 'standalone'
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './StandaloneDehumidifierV1.css'

export const DEHUMIDIFIER_STANDALONE_V1_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Background', icon: 'smartphone', slots: [] },
  { id: 'header', label: 'Device Header', icon: 'title', slots: [
    { id: 'header-name', label: 'Device Name', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding', 'borderRadius'] },
    { id: 'header-status', label: 'Status Text', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
    { id: 'header-dot', label: 'Status Dot', editableProps: ['background', 'width', 'height', 'borderRadius'] },
    { id: 'header-power', label: 'Power Button', editableProps: ['background', 'color', 'borderRadius', 'border', 'padding'] },
    { id: 'header-comfort', label: 'Comfort Badge', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
  ]},
  { id: 'dial', label: 'Humidity Dial', icon: 'humidity_percentage', slots: [
    { id: 'dial-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'dial-value', label: 'Humidity Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'dial-unit', label: 'Unit Text', editableProps: ['fontSize', 'color'] },
    { id: 'dial-comfort', label: 'Comfort Label', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'dial-target-btn', label: '+/- Button', editableProps: ['background', 'color', 'borderRadius', 'border'] },
    { id: 'dial-target-value', label: 'Target Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
  { id: 'mode', label: 'Mode Selector', icon: 'auto_mode', slots: [
    { id: 'mode-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
  ]},
  { id: 'fan', label: 'Fan Speed', icon: 'mode_fan', slots: [
    { id: 'fan-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'fan-btn', label: 'Fan Button', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius', 'border'] },
  ]},
  { id: 'tank', label: 'Water Tank', icon: 'water_drop', slots: [
    { id: 'tank-label', label: 'Tank Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'tank-pct', label: 'Tank Percentage', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'tank-bar', label: 'Progress Bar', editableProps: ['background', 'borderRadius', 'height'] },
    { id: 'tank-hint', label: 'Hint Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
  { id: 'chart', label: 'Humidity History', icon: 'bar_chart', slots: [
    { id: 'chart-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'chart-title', label: 'Chart Title', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'chart-range-btn', label: 'Range Button', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
    { id: 'chart-bar', label: 'Bar', editableProps: ['background', 'borderRadius'] },
  ]},
]

const DEFAULT_SECTION_ORDER = DEHUMIDIFIER_STANDALONE_V1_SECTIONS.map(s => s.id)

const HUMIDITY_MIN = 30
const HUMIDITY_MAX = 80

// Helpers
function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) { if (key in state && state[key] != null) return state[key] }
  return undefined
}
function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v == null) return null
  if (Array.isArray(v)) v = v[0]
  if (v == null) return null
  const n = Number(v); return isNaN(n) ? null : n
}
function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys); return v != null ? String(v) : ''
}

function comfortLevel(humidity: number): { label: string; cls: string } {
  if (humidity < 30) return { label: 'Too Dry', cls: 'dry' }
  if (humidity <= 60) return { label: 'Comfortable', cls: 'ok' }
  return { label: 'Too Humid', cls: 'humid' }
}

// SVG ring helpers
const RING_R = 70
const RING_CX = 90
const RING_CY = 90
const RING_CIRC = 2 * Math.PI * RING_R
const RING_ARC = RING_CIRC * 0.75 // 270 degrees

function humidityToDash(humidity: number): number {
  const pct = Math.max(0, Math.min(100, humidity)) / 100
  return RING_ARC - pct * RING_ARC
}

const MOCK_STATE: Record<string, unknown> = {
  on: true,
  humidityAmbientPercent: 72,
  humiditySetpointPercent: 55,
  currentModeSettings: 'auto',
  currentFanSpeedSetting: 'medium',
  waterTankLevel: 65,
  waterTankFull: false,
}

const MODES = [
  { id: 'auto', label: 'Auto', icon: 'auto_awesome' },
  { id: 'continuous', label: 'Continuous', icon: 'all_inclusive' },
  { id: 'laundry', label: 'Laundry', icon: 'local_laundry_service' },
  { id: 'sleep', label: 'Sleep', icon: 'bedtime' },
]

const FAN_SPEEDS = ['Low', 'Med', 'High', 'Auto']
const FAN_MAP: Record<string, string> = { low: 'Low', medium: 'Med', high: 'High', auto: 'Auto' }

export function StandaloneDehumidifierV1({
  device, deviceState, onCommand, onClose, isEditorMode, cssOverrides,
  sectionOrder, sectionVisible, highlightedSection, credentials,
}: DeviceModalTemplateProps) {

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    return [...order, ...DEFAULT_SECTION_ORDER.filter(id => !order.includes(id))]
  }, [sectionOrder])
  const isSectionVisible = useCallback((id: string) => sectionVisible?.[id] !== false, [sectionVisible])

  // Effective state
  const hasData = deviceState && Object.keys(deviceState).some(k =>
    ['humidityAmbientPercent', 'humidity', 'humiditySetpointPercent', 'targetHumidity'].includes(k))
  const effectiveState = hasData ? deviceState : { ...MOCK_STATE, ...(deviceState || {}) }

  const isOn = Boolean(getAttr(effectiveState, 'on'))
  const currentHumidity = numAttr(effectiveState, 'humidityAmbientPercent', 'humidity', 'currentHumidity') ?? 0
  const targetHumidity = numAttr(effectiveState, 'humiditySetpointPercent', 'targetHumidity', 'humiditySetpoint') ?? 55
  const currentMode = strAttr(effectiveState, 'currentModeSettings', 'mode') || 'auto'
  const currentFan = strAttr(effectiveState, 'currentFanSpeedSetting', 'fanSpeed') || 'medium'
  const tankLevel = numAttr(effectiveState, 'waterTankLevel', 'tankLevel') ?? 0
  const tankFull = Boolean(getAttr(effectiveState, 'waterTankFull', 'tankFull'))
  const comfort = comfortLevel(currentHumidity)

  // Pending states
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const [pendingHumidity, setPendingHumidity] = useState<number | null>(null)
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingFan, setPendingFan] = useState<string | null>(null)

  const displayPower = pendingPower ?? isOn
  const displayTarget = pendingHumidity ?? targetHumidity
  const displayMode = pendingMode ?? currentMode
  const displayFan = pendingFan ?? currentFan

  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])
  useEffect(() => { if (pendingHumidity !== null && pendingHumidity === targetHumidity) setPendingHumidity(null) }, [targetHumidity, pendingHumidity])
  useEffect(() => { if (pendingMode !== null && pendingMode === currentMode) setPendingMode(null) }, [currentMode, pendingMode])
  useEffect(() => { if (pendingFan !== null && pendingFan === currentFan) setPendingFan(null) }, [currentFan, pendingFan])

  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const togglePower = useCallback(() => {
    const next = !displayPower; setPendingPower(next); execCmd(next ? 'on' : 'off')
  }, [displayPower, execCmd])

  const adjustHumidity = useCallback((delta: number) => {
    if (!displayPower) return
    const next = Math.max(HUMIDITY_MIN, Math.min(HUMIDITY_MAX, displayTarget + delta))
    setPendingHumidity(next)
    execCmd('setHumidity', { humidity: next })
  }, [displayPower, displayTarget, execCmd])

  const setMode = useCallback((mode: string) => {
    if (!displayPower) return
    setPendingMode(mode); execCmd('setMode', { mode })
  }, [displayPower, execCmd])

  const setFan = useCallback((speed: string) => {
    if (!displayPower) return
    const mapped = speed === 'Med' ? 'medium' : speed.toLowerCase()
    setPendingFan(mapped); execCmd('setFanSpeed', { fanSpeed: mapped })
  }, [displayPower, execCmd])

  // Settings
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])
  const startRename = useCallback(() => { setRenameValue(displayName); setIsRenaming(true); setTimeout(() => renameInputRef.current?.focus(), 50) }, [displayName])
  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed); setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {}); setCopiedField(label); setTimeout(() => setCopiedField(null), 1000)
  }, [])
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])

  // Chart mock
  const [chartRange, setChartRange] = useState<'day' | 'week' | 'month'>('day')
  const chartData = useMemo(() => {
    const n = chartRange === 'day' ? 12 : chartRange === 'week' ? 7 : 30
    return Array.from({ length: n }, (_, i) => ({
      pct: 30 + Math.random() * 60,
      active: chartRange === 'day' ? i === 4 : i === n - 3,
    }))
  }, [chartRange])

  const stateClass = displayPower ? 'sdh--on' : 'sdh--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null
    switch (sectionId) {
      case 'header':
        return (
          <div key="header" className={`sdh__header${hl('header')}`} data-modal-section="header">
            <div className="sdh__header-top">
              <div>
                <div className="sdh__header-name" data-modal-slot="header-name">{displayName}</div>
                <div className="sdh__header-status" data-modal-slot="header-status">
                  <span className={`sdh__status-dot${device.online !== false ? ' sdh__status-dot--online' : ''}`} data-modal-slot="header-dot" />
                  {device.online !== false ? 'Online' : 'Offline'}
                </div>
              </div>
              <button className={`sdh__power-btn${displayPower ? ' sdh__power-btn--on' : ''}`}
                data-modal-slot="header-power" onClick={togglePower}>
                <span className="material-symbols-outlined" style={{ fontSize: 22 }}>power_settings_new</span>
              </button>
            </div>
            {displayPower && (
              <div className={`sdh__header-comfort sdh__header-comfort--${comfort.cls}`} data-modal-slot="header-comfort">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>humidity_percentage</span>
                {currentHumidity}% — {comfort.label}
              </div>
            )}
          </div>
        )

      case 'dial':
        return (
          <div key="dial" className={`sdh__dial${hl('dial')}`} data-modal-section="dial">
            <div className="sdh__dial-label" data-modal-slot="dial-label">Humidity Level</div>
            <div className="sdh__dial-ring">
              <svg className="sdh__dial-svg" viewBox="0 0 180 180">
                {/* Background arc */}
                <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none"
                  stroke="rgba(45,45,45,0.08)" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${RING_ARC} ${RING_CIRC}`}
                  transform={`rotate(135 ${RING_CX} ${RING_CY})`} />
                {/* Value arc */}
                <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none"
                  stroke="#7c3aed" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${RING_ARC} ${RING_CIRC}`}
                  strokeDashoffset={humidityToDash(displayPower ? currentHumidity : 0)}
                  transform={`rotate(135 ${RING_CX} ${RING_CY})`}
                  style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
              </svg>
              <div className="sdh__dial-center">
                <span className="sdh__dial-value" data-modal-slot="dial-value">{displayPower ? currentHumidity : '--'}</span>
                <span className="sdh__dial-unit" data-modal-slot="dial-unit">%RH</span>
                {displayPower && (
                  <span className={`sdh__dial-comfort sdh__dial-comfort--${comfort.cls}`} data-modal-slot="dial-comfort">
                    {comfort.label}
                  </span>
                )}
              </div>
            </div>
            <div className="sdh__dial-target">
              <button className="sdh__dial-target-btn" data-modal-slot="dial-target-btn"
                disabled={!displayPower} onClick={() => adjustHumidity(-5)}>−</button>
              <span className="sdh__dial-target-value" data-modal-slot="dial-target-value">
                Target {displayPower ? displayTarget : '--'}%
              </span>
              <button className="sdh__dial-target-btn" data-modal-slot="dial-target-btn"
                disabled={!displayPower} onClick={() => adjustHumidity(5)}>+</button>
            </div>
          </div>
        )

      case 'mode':
        return (
          <div key="mode" className={`sdh__modes${hl('mode')}`} data-modal-section="mode">
            <div className="sdh__modes-label" data-modal-slot="mode-label">Operation Mode</div>
            <div className="sdh__modes-pill">
              {MODES.map(m => (
                <button key={m.id}
                  className={`sdh__mode-btn${displayMode === m.id ? ' active' : ''}`}
                  data-modal-slot="mode-btn"
                  onClick={() => setMode(m.id)}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{m.icon}</span>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        )

      case 'fan':
        return (
          <div key="fan" className={`sdh__fan${hl('fan')}`} data-modal-section="fan">
            <div className="sdh__fan-label" data-modal-slot="fan-label">Fan Speed</div>
            <div className="sdh__fan-buttons">
              {FAN_SPEEDS.map(s => {
                const mapped = s === 'Med' ? 'medium' : s.toLowerCase()
                return (
                  <button key={s}
                    className={`sdh__fan-btn${displayFan === mapped ? ' active' : ''}`}
                    data-modal-slot="fan-btn"
                    onClick={() => setFan(s)}>
                    {s}
                  </button>
                )
              })}
            </div>
          </div>
        )

      case 'tank':
        return (
          <div key="tank" className={`sdh__tank${hl('tank')}`} data-modal-section="tank">
            <div className="sdh__tank-header">
              <span className="sdh__tank-label" data-modal-slot="tank-label">Water Tank</span>
              <span className={`sdh__tank-pct${tankFull ? ' sdh__tank-pct--full' : ''}`} data-modal-slot="tank-pct">
                {displayPower ? `${tankLevel}%` : '--'}
              </span>
            </div>
            <div className="sdh__tank-bar" data-modal-slot="tank-bar">
              <div className={`sdh__tank-bar-fill${tankFull ? ' sdh__tank-bar-fill--full' : ''}`}
                style={{ width: `${displayPower ? tankLevel : 0}%` }} />
            </div>
            <div className={`sdh__tank-hint${tankFull ? ' sdh__tank-hint--full' : ''}`} data-modal-slot="tank-hint">
              {tankFull ? 'Tank Full — Please Empty' : `~${Math.round((100 - tankLevel) * 0.3)} hours until full`}
            </div>
          </div>
        )

      case 'chart':
        return (
          <div key="chart" className={`sdh__chart${hl('chart')}`} data-modal-section="chart">
            <div className="sdh__chart-header">
              <div className="sdh__chart-header-left">
                <span className="sdh__chart-label" data-modal-slot="chart-label">Humidity Metrics</span>
                <span className="sdh__chart-title" data-modal-slot="chart-title">History</span>
              </div>
              <div className="sdh__chart-range">
                {(['day', 'week', 'month'] as const).map(r => (
                  <button key={r} className={`sdh__chart-range-btn${chartRange === r ? ' sdh__chart-range-btn--active' : ''}`}
                    data-modal-slot="chart-range-btn" onClick={() => setChartRange(r)}>
                    {r === 'day' ? 'Day' : r === 'week' ? 'Week' : 'Month'}
                  </button>
                ))}
              </div>
            </div>
            <div className="sdh__chart-bars">
              {chartData.map((d, i) => (
                <div key={i} className={`sdh__chart-bar${d.active ? ' sdh__chart-bar--active' : ''}`}
                  data-modal-slot="chart-bar" style={{ height: `${d.pct}%` }} />
              ))}
            </div>
          </div>
        )

      default: return null
    }
  }

  return (
    <div className={`sdh ${stateClass}`} data-modal-template-id="dehumidifier-standalone-v1">
      {cssOverrides && <style>{cssOverrides}</style>}
      {orderedSections.map(id => renderSection(id))}

      <button className="sdh__settings-btn" data-modal-slot="settings-btn"
        onClick={() => setSettingsOpen(!settingsOpen)}>
        <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
          {settingsOpen ? 'close' : 'settings'}
        </span>
      </button>

      {settingsOpen && (
        <div className="sdh__settings-panel">
          <div className="sdh__settings-section">
            <div className="sdh__settings-section-title">Device Name</div>
            {isRenaming ? (
              <div className="sdh__settings-row">
                <input ref={renameInputRef} className="sdh__settings-input" value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                  onBlur={submitRename} maxLength={40} />
              </div>
            ) : (
              <div className="sdh__settings-row">
                <span className="sdh__settings-value">{displayName}</span>
                <button className="sdh__settings-edit" onClick={startRename}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                </button>
              </div>
            )}
          </div>
          <div className="sdh__settings-section">
            <div className="sdh__settings-section-title">Device Info</div>
            {deviceInfoItems.map(item => (
              <div key={item.label} className="sdh__settings-row">
                <span className="sdh__settings-label">{item.label}</span>
                <span className="sdh__settings-value">{item.value}</span>
                {item.action === 'copy' && (
                  <button className="sdh__settings-copy" onClick={() => copyToClipboard(item.value, item.label)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span>
                    {copiedField === item.label && <span className="sdh__settings-copied">Copied</span>}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
